import React, { useEffect, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";

import { Skeleton, Breadcrumbs } from "components";
import { getErrorMessage } from "helpers";
import useLoader from "hooks/useLoader";
import { pendActions, uiActions } from "store";
import {
	GetPend,
	GetPendHistory,
	GetPendList,
	NameId,
	PendHistory,
	PendType,
	SetPaging,
} from "types";

import HistoryPend from "./historyPend";
import UpdatePendForm from "./updatePendForm";

const PAGE_SIZE = 50;

type UpdatePendProps = {
	loading: boolean;
	pend: PendType;
	getPend: GetPend;
	history: PendHistory[];
	historyLoading: boolean;
	historyCount: number;
	historyPage: number;
	getPendHistory: GetPendHistory;
	setPagingPendHistory: SetPaging;
	getPendList: GetPendList;
	openPendList: PendType[];
	pages?: any[];
	nameId?: NameId;
};

const UpdatePend = ({
	loading = false,
	pend,
	getPend,
	history,
	historyLoading,
	historyPage,
	historyCount,
	getPendHistory,
	getPendList,
	openPendList,
	setPagingPendHistory,
	pages = [],
	nameId,
}: UpdatePendProps) => {
	const params = useParams<{ idPend: string; id: string }>();
	const dispatch = useDispatch();
	const historyBrowser = useHistory();

	useEffect(() => {
		if (params?.id && nameId) {
			getPendList(nameId, params?.id, PAGE_SIZE, "closed");
		}
	}, [getPendList, params?.id, nameId]);

	const loadPend = useCallback(
		() =>
			getPend(params.idPend).then(
				pend => {
					pend?.id !== 0 && getPendHistory(PAGE_SIZE, pend?.id);
				},
				error => {
					dispatch(
						uiActions.showErrorNotification({
							title: getErrorMessage(error),
						}),
					);
				},
			),
		[dispatch, getPend, getPendHistory, params.idPend],
	);

	useEffect(() => {
		loadPend();
	}, [loadPend]);

	const handleBreadcrumb = newStatus => {
		const currentTab = pages[0]?.link;

		const isNoAnotherOpenPend =
			currentTab === "/underwriting/pended" &&
			openPendList.length === 1 &&
			newStatus === "closed";

		if (isNoAnotherOpenPend) {
			historyBrowser.push(
				`/underwriting/in-review/${params?.id}/pend/${params?.idPend}`,
			);
		}
	};

	const handleHistoryPageChange = page => {
		setPagingPendHistory(page - 1);
		getPendHistory(PAGE_SIZE, pend?.id);
	};

	const Loader = useLoader(loading);
	const PendNumber = () => (
		<Loader
			skeleton={
				<Skeleton
					variant="text"
					width={100}
					marginLeft={10}
					marginBottom={3}
					marginTop={7}
				/>
			}
			component={pend?.number}
		/>
	);

	return (
		<>
			<Breadcrumbs
				pages={[
					...pages,
					{
						title: "Pend -",
					},
				]}
				component={<PendNumber />}
			/>
			<div className="forms-container mt-5">
				{loading ? (
					<Skeleton height="250px" />
				) : (
					<>
						<UpdatePendForm
							loading={false}
							pend={pend}
							reload={loadPend}
							handleBreadcrumb={handleBreadcrumb}
						/>
						<HistoryPend
							history={history}
							loading={historyLoading}
							page={historyPage}
							count={historyCount}
							handleHistoryPageChange={handleHistoryPageChange}
						/>
					</>
				)}
			</div>
		</>
	);
};

const mapStateToProps = state => ({
	pend: state.pend.pend,
	loading: state.pend.loading,
	openPendList: state.pend.pendDetails.list,
	history: state.pend.pendHistory.list,
	historyLoading: state.pend.pendHistory.loading,
	historyCount: state.pend.pendHistory.count,
	historyPage: state.pend.pendHistory.page,
});

const mapDispatchToProps = {
	getPend: pendActions.getPend,
	getPendList: pendActions.getPendList,
	getPendHistory: pendActions.getPendHistory,
	setPagingPendHistory: pendActions.setPagingPendHistory,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdatePend);
